/*
 * ClientDashboard — platform-shaped dashboard for client/client_admin.
 *
 * Replaces the legacy UnifiedAnalytics view (chatbot-era stats: messages,
 * documents, KB doughnut) with surfaces that map to the new platform:
 *
 *   1. Welcome card — name + tier pill + dossier completeness
 *   2. Routing status — where the user is in the Orbit → validx flow
 *   3. Agent builds — projects in flight with status/phase
 *   4. Quota meters — chatbot turns / agent requests / consultant sessions
 *                     / seats, all tier-aware
 *   5. Pending TODOs — relocated from the sidebar (workspace.pending)
 *   6. Quick links — MyBusiness tabs + Marketplace + Team
 *
 * Backend reads: /api/workspace/me (tier + pending + consultant + members),
 *                /api/intake/state/:client_id (route_plan + completeness),
 *                /api/agents/projects (in-flight builds).
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Compass, Layers, Hammer, MessageSquare, Users, Building2,
  ShoppingBag, ChevronRight, ArrowRight, AlertCircle, Loader2, Bot,
  CheckCircle2,
} from 'lucide-react';
import { TierPill } from '../../components/billing/TierPill';
import BuildStatsDashboard from '../../components/agents/workspace/BuildStatsDashboard';
import { requestAgentBridgeUrl, getClientDashboardOverview } from '../../services/api';
import { useParticipant } from '../../hooks/useParticipant';
import { setupApi } from '../../services/setupApi';
import RoleDashboard from '../aai/dashboards/RoleDashboard';
import EmployeeHome from '../employee/EmployeeHome';
import OwnerHome from '../owner/OwnerHome';
import DepartmentHome from '../department/DepartmentHome';
import ChampionHome from '../champion/ChampionHome';
import TrainerHome from '../trainer/TrainerHome';

const API_BASE = process.env.REACT_APP_API_URL ?? '';


interface QuotaUsage {
  label: string;
  used: number;
  limit: number;        // -1 = unlimited
  unit: string;
  icon: React.ComponentType<any>;
}


function pct(used: number, limit: number): number {
  if (limit === -1) return 0;
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}


// Tier → quota allowances. Kept in lock-step with the new platform tier
// matrix in backend/migrations/.../2026_05_15_platform_tiers.py. Source of
// truth is the workspace plan row; this fallback table matches it.
const TIER_QUOTAS: Record<string, { chatbot_turns: number; agent_requests: number; consultant_sessions: number; team_seats: number }> = {
  free:        { chatbot_turns: 100,  agent_requests: 1,  consultant_sessions: 0,  team_seats: 1  },
  workspace:   { chatbot_turns: -1,   agent_requests: -1, consultant_sessions: -1, team_seats: -1 },
  solo:        { chatbot_turns: 1000, agent_requests: 1,  consultant_sessions: 1,  team_seats: 3  },
  team:        { chatbot_turns: 5000, agent_requests: 3,  consultant_sessions: 3,  team_seats: 8  },
  scale:       { chatbot_turns: -1,   agent_requests: 10, consultant_sessions: -1, team_seats: 25 },
  custom:      { chatbot_turns: -1,   agent_requests: -1, consultant_sessions: -1, team_seats: -1 },
};


export default function ClientDashboard() {
  const navigate = useNavigate();
  // A member who started the setup and has not finished it goes back to it.
  // Members who were here before the setup existed never started one.
  useEffect(() => {
    if (localStorage.getItem('impersonated_client_id')) return;
    setupApi.get()
      .then(({ data }) => { if (data.active) navigate('/setup', { replace: true }); })
      .catch(() => {});
  }, [navigate]);
  const clientId = useMemo(
    () => localStorage.getItem('client_id') || localStorage.getItem('impersonated_client_id'),
    [],
  );
  const userName = useMemo(() => {
    const raw = localStorage.getItem('user_name') || '';
    return raw.trim().split(/\s+/)[0] || null;
  }, []);

  const [workspace, setWorkspace] = useState<any>(null);
  const [intake, setIntake] = useState<any>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Participants and department heads get a readiness-only dashboard — skip the
  // owner-facing workspace/intake/build fetches entirely.
  const { isParticipant, isDepartmentHead, role, ready: roleReady } = useParticipant();
  const isOwner = role === 'OWNER';
  const isChampion = role === 'AI_CHAMPION';
  const isTrainer = role === 'TRAINER';
  const readinessOnly = isParticipant || isDepartmentHead || isOwner || isChampion || isTrainer;

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    if (roleReady && readinessOnly) { setLoading(false); return; }
    if (!roleReady) return; // wait until we know whether to fetch owner data
    let cancelled = false;
    const load = async () => {
      try {
        const [wsRes, intRes, projRes, ovRes] = await Promise.allSettled([
          axios.get(`${API_BASE}/api/workspace/me`),
          axios.get(`${API_BASE}/api/intake/state/${clientId}`),
          axios.get(`${API_BASE}/api/agents/projects`),
          getClientDashboardOverview(clientId),
        ]);
        if (cancelled) return;
        if (wsRes.status === 'fulfilled') setWorkspace(wsRes.value.data);
        if (intRes.status === 'fulfilled') setIntake(intRes.value.data);
        if (projRes.status === 'fulfilled') {
          const list = projRes.value.data?.projects || projRes.value.data || [];
          setProjects(Array.isArray(list) ? list : []);
        }
        if (ovRes.status === 'fulfilled') setOverview(ovRes.value);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clientId, roleReady, readinessOnly]);

  const tier = (workspace?.plan_tier || 'free') as keyof typeof TIER_QUOTAS;
  const tierQuotas = TIER_QUOTAS[tier] || TIER_QUOTAS.free;
  const inFlight = projects.filter(p => ['PENDING', 'ASSIGNED', 'IN_PROGRESS', 'TESTING'].includes(p.status));

  // Real usage from the one aggregate (falls back to local counts if the
  // aggregate is briefly unavailable). No fabricated zeros: a metric the
  // aggregate can't compute simply keeps its honest local value.
  const oq = overview?.quota || {};
  const usedAgentRequests = oq.builds?.used ?? inFlight.length;
  const seatsUsed = oq.team_seats?.used ?? (workspace?.members?.length ?? 1);
  const chatTurnsUsed = oq.chat_turns?.used ?? 0;
  const quotas: QuotaUsage[] = [
    { label: 'Agent builds',   used: usedAgentRequests, limit: oq.builds?.limit ?? tierQuotas.agent_requests, unit: '/month', icon: Hammer },
    { label: 'Team seats',     used: seatsUsed,         limit: oq.team_seats?.limit ?? tierQuotas.team_seats, unit: '',       icon: Users },
    { label: 'Chatbot turns',  used: chatTurnsUsed,     limit: oq.chat_turns?.limit ?? tierQuotas.chatbot_turns, unit: '/month', icon: MessageSquare },
  ];

  // Executive top line — real spend + delivery + pipeline from the aggregate.
  const sp = overview?.spend || {};
  const dl = overview?.delivery || {};
  const pl = overview?.pipeline || {};
  const money = (n: number) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const kpis = overview ? [
    { label: 'Spend · 30d', value: money(sp.total_usd_30d), sub: `${money(sp.chat_cost_usd_30d)} chat · ${money(sp.builder_cost_usd_30d)} build`, tone: 'ink' },
    { label: 'In flight',   value: String(dl.in_progress ?? inFlight.length), sub: `${dl.projects ?? projects.length} projects`, tone: 'brand' },
    { label: 'Shipped',     value: String(dl.shipped ?? 0), sub: 'builds delivered', tone: 'good' },
    { label: 'Open requests', value: String(dl.open_tickets ?? 0), sub: (dl.open_tickets ? 'needs input' : 'all clear'), tone: (dl.open_tickets ? 'warn' : 'muted') },
  ] : [];

  if (!roleReady || loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  // An employee's home is "Your work": what they can run now, what they have in
  // flight, and the one adoption step that is next. It carries its own page
  // head, so there is no welcome banner above it.
  if (isParticipant) {
    return <EmployeeHome />;
  }

  // The Owner's home is decisions first: what needs them, then the portfolio
  // snapshot. It carries its own page head.
  if (isOwner) {
    return <OwnerHome />;
  }

  // The Department Head's home is the team's attention queue: the decision
  // waiting on them, shared patterns, department work, who needs support.
  if (isDepartmentHead) {
    return <DepartmentHome />;
  }

  // The AI Champion's home is the portfolio: what waits on their decision,
  // the current cycle, the ranked priorities and what is ready to reuse.
  if (isChampion) {
    return <ChampionHome />;
  }

  // The Trainer's home is the delivery queue: what the company has asked of
  // them, the sessions ahead, and the teams to target support at.
  if (isTrainer) {
    return <TrainerHome />;
  }

  // Department-head dashboard: the designed role-based home (team rollup +
  // approvals), not the owner-facing usage/todos.
  if (readinessOnly) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {userName ? `Welcome, ${userName}` : 'Welcome'}
          </h1>
          <p className="text-sm text-gray-500">Your AI growth — coaching, not surveillance.</p>
        </div>
        <RoleDashboard />
      </div>
    );
  }

  const toneColor: Record<string, string> = {
    ink: 'text-gray-900', brand: 'text-brand-600', good: 'text-green-600', warn: 'text-amber-600', muted: 'text-gray-400',
  };

  // Onboarding is not finished, so offer the way back into it. `postLoginRoute`
  // sends every returning email login here rather than to /orbit/intake, on the
  // grounds that "the dashboard surfaces any pending intake as a to-do" — and it
  // did not: this page already fetched the intake state and then never rendered
  // it, so anyone whose first ever login was a password login (the normal case
  // for a consultant-created account) landed here with no route into onboarding
  // at all. The route plan supplies its own label, reason and destination.
  // Not named `pct`: that is the module-level quota helper below, and shadowing
  // it turned the quota grid's `pct(q.used, q.limit)` into a call on a number.
  // Type checking was switched back on for this file in the same change, so the
  // compiler catches that shape now instead of the browser.
  // completion_pct is a 0 to 1 share, not a percentage.
  const intakePct = Number(intake?.company_context?.completion_pct ?? 1) * 100;
  const nextAction = intake?.route_plan?.next_best_action ?? null;
  const showIntakeTodo = Number.isFinite(intakePct) && intakePct < 100;

  return (
    <div className="space-y-6">
      {showIntakeTodo && (
        <section className="rounded-xl border border-brand-200 bg-brand-50 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-[15px] font-semibold text-gray-900">
                {nextAction?.label || 'Finish mapping your business'}
              </h2>
              <p className="mt-0.5 text-[13px] leading-snug text-gray-600">
                {nextAction?.reason
                  || 'We use this to make everything that follows specific to you rather than generic.'}
              </p>
            </div>
            <div className="flex flex-none items-center gap-3">
              <span className="font-sans text-[11px] tabular-nums text-gray-500">
                {Math.round(intakePct)}% done
              </span>
              <button
                type="button"
                onClick={() => navigate(nextAction?.route || '/orbit/intake')}
                className="rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-brand-700"
              >
                Pick up where you left off
              </button>
            </div>
          </div>
        </section>
      )}
      {/* Executive top line — real spend + delivery from the one aggregate.
          Renders only when the aggregate resolved; never fabricates. */}
      {kpis.length > 0 && (
        <section>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {kpis.map(k => (
              <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide font-sans">{k.label}</div>
                <div className={`text-[26px] font-bold font-sans tabular-nums mt-1.5 leading-none ${toneColor[k.tone] || 'text-gray-900'}`}>{k.value}</div>
                <div className="text-[11px] text-gray-400 mt-1.5 font-sans">{k.sub}</div>
              </div>
            ))}
          </div>
          {/* Pipeline funnel — scoped → PRD ready → shipped, from real counts. */}
          {(pl.scoped || pl.prd_ready || pl.shipped) ? (
            <div className="mt-3 bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Compass size={14} className="text-gray-400" strokeWidth={2} />
                <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide font-sans">Pipeline</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {[['Scoped', pl.scoped || 0], ['PRD ready', pl.prd_ready || 0], ['Shipped', pl.shipped || 0]].map(([label, n], i) => (
                  <React.Fragment key={label as string}>
                    {i > 0 && <ChevronRight size={14} className="text-gray-300 shrink-0" />}
                    <div className="flex-1 rounded-lg bg-brand-50/60 px-3 py-2">
                      <div className="text-lg font-bold font-sans tabular-nums text-gray-900 leading-none">{n as number}</div>
                      <div className="text-[10.5px] text-gray-500 mt-1 font-sans uppercase tracking-wide">{label}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      )}

      {/* This month — compact usage status strip; the at-a-glance header
          now that the welcome card is gone (carries tier limits + usage). */}
      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Layers size={14} strokeWidth={2.25} />
            This month
          </h2>
          <Link to="/client/billing" className="text-xs font-medium text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
            Manage billing
            <ChevronRight size={12} strokeWidth={2.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {quotas.map(q => {
            const QIcon = q.icon;
            const unlimited = q.limit === -1;
            const percentage = unlimited ? 0 : pct(q.used, q.limit);
            return (
              <div key={q.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <QIcon size={14} className="text-gray-400" strokeWidth={2} />
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide font-sans">
                    {q.label}
                  </span>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-2xl font-bold text-gray-900 font-sans">{q.used}</span>
                  <span className="text-xs text-gray-400 font-sans">
                    / {unlimited ? '∞' : q.limit}{q.unit}
                  </span>
                </div>
                {!unlimited && (
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${percentage}%`,
                        background: percentage >= 90 ? '#fd2751' : percentage >= 70 ? '#f59e0b' : '#18191d',
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Your agent — one zone: live builds (when running) + the
          cumulative build telemetry beneath. Client-resolved telemetry, so it
          shows even when the projects list is empty. */}
      {clientId && (
        <section>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Hammer size={14} strokeWidth={2.25} />
              Your agent
            </h2>
            {projects.length > 0 && (
              <Link to="/agents" className="text-xs font-medium text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
                All agents
                <ChevronRight size={12} strokeWidth={2.5} />
              </Link>
            )}
          </div>
          {inFlight.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {inFlight.slice(0, 4).map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={async () => {
                    // Workspace lives on validx — open via the HMAC bridge.
                    try {
                      const data = await requestAgentBridgeUrl(
                        `/customer/project/${p.id}/builder/?clientId=${clientId}`,
                        clientId || undefined,
                      );
                      if (data?.url) { window.open(data.url, '_blank', 'noopener,noreferrer'); return; }
                    } catch (e) { console.warn('[dashboard] workspace bridge failed', e); }
                  }}
                  className="text-left w-full bg-white rounded-xl border border-gray-200 p-4 hover:border-brand-200 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center flex-shrink-0">
                      {p.status === 'IN_PROGRESS' ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.title}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 inline-flex items-center gap-1.5">
                        <span className="font-sans uppercase tracking-wide">{p.status}</span>
                        {typeof p.currentPhase === 'number' && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>Phase {p.currentPhase}</span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <BuildStatsDashboard clientId={clientId} embedded />
        </section>
      )}

      {/* To-do widget removed from the dashboard (CTO 08-13). */}

    </div>
  );
}


function QuickLink({ to, icon: Icon, label }: { to: string; icon: React.ComponentType<any>; label: string }) {
  return (
    <Link
      to={to}
      className="group bg-white rounded-xl border border-gray-200 px-4 py-3 hover:border-brand-200 hover:shadow-sm transition-all flex items-center justify-between"
    >
      <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-2">
        <Icon size={14} strokeWidth={2} className="text-gray-400 group-hover:text-brand-500 transition-colors" />
        {label}
      </span>
      <ArrowRight size={14} className="text-gray-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" strokeWidth={2.5} />
    </Link>
  );
}
